import React, { useCallback, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from "@material-ui/icons";
import { ActiveStatus } from "src/apps/commons/dataDisplays";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { formatDateTime, useDebounce } from "src/utils";
import { AsyncDropdown } from "../../../commons/dropdowns";
import { listAllPartners } from "../../../commons/dropdowns/api";
import { IPartnerBrief } from "../../partner/types";
import * as Api from "../api";
import { useFullData } from "../hooks";
import {
  IPartnerBrand,
  IRemyndBrandPartnerMap,
  IRemyndBrandPartnerMapSaveReq,
  PartnerBrandWithMapping
} from "../types";
import {
  BrandSortColumn,
  combineWithMappingData,
  filterListItems,
  sortList
} from "../utils";
import {
  CellRenderer,
  RowGetter,
  VirtualizedTable
} from "./ListVirtualizedTable";
import MappingModal from "./MappingModal";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    height: "calc(100vh - 128px)",
    backgroundColor: theme.palette.background.default,
    display: "flex",
    flexDirection: "column"
  },
  tableContainer: {
    flexGrow: 1
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  margin: {
    margin: theme.spacing(1)
  },
  ml1: { marginLeft: theme.spacing(1) },
  ml2: { marginLeft: theme.spacing(2) },
  mr1: { marginRight: theme.spacing(1) },
  mr2: { marginRight: theme.spacing(2) },

  toolbarRoot1: {
    padding: theme.spacing(2, 2, 0, 2),
    minHeight: 48
  },
  toolbarRoot2: {
    padding: theme.spacing(1, 2, 2, 2),
    minHeight: 48
  },
  toolbarInner: {
    flex: "1 1 auto",
    justifyContent: "flex-start"
  },
  toolbarEnd: {
    flex: "0 1 auto",
    flexWrap: "nowrap",
    justifyContent: "flex-end"
  },
  searchTerm: {
    minWidth: 400,
    fontSize: "0.875rem",
    "& .MuiOutlinedInput-adornedEnd": {
      paddingRight: 0
    }
  },

  inactive: {
    opacity: 0.6
  },
  tableCell: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  tableCellText: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden"
  },
  countText: {
    color: theme.palette.primary.main,
    fontSize: "0.75rem",
    fontWeight: 400
  }
}));

const parseBooleanRadio = (input: string) =>
  input === "true" ? true : input === "false" ? false : undefined;
const formatBooleanRadio = (input?: boolean) =>
  input === undefined ? "" : input.toString();

const boolColumnWidth = 80;
const dateColumnWidth = 180;
const flexibleWidth = (maxWidth: number) =>
  Math.max(240, maxWidth - (boolColumnWidth + 2 * dateColumnWidth));

// ---------------------------------------------------------------------------

export type SortState = Readonly<{
  column: BrandSortColumn;
  desc: boolean;
}>;

export type EditModalState = Readonly<{
  open: boolean;
  partnerBrand?: IPartnerBrand;
  mapping?: IRemyndBrandPartnerMap;
}>;

const ListContainers: React.FC<unknown> = _ => {
  const classes = useStyles();
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [partner, setPartner] = useState<IDropdown<IPartnerBrief> | null>(null);

  const {
    state,
    fetchFullData: fetchData,
    dropdowns
  } = useFullData(partner?.value);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { data, fetching } = state;

  // -- APIs -----------------------------------------------------------------

  const callDelete = useCallback(
    async (req: ICommonIdsReq) => {
      setSubmitting(true);
      let success = true;
      try {
        await Api.deleteMultiple(req);
        setMessage("success", `ลบ Mapping แบรนด์ สำเร็จ`);
        await fetchData();
      } catch (e) {
        handleError(e);
        success = false;
      } finally {
        setSubmitting(false);
      }
      return success;
    },
    [handleError, fetchData, setMessage]
  );

  const callSubmit = useCallback(
    async (
      id: RemyndBrandPartnerMapId | undefined,
      req: IRemyndBrandPartnerMapSaveReq
    ) => {
      setSubmitting(true);
      let success = true;
      try {
        if (id == null) {
          await Api.save(req);
        } else {
          await Api.update(id, req);
        }
        setMessage(
          "success",
          `${id ? "แก้ไข" : "สร้าง"} Mapping แบรนด์ สำเร็จ`
        );
        await fetchData();
      } catch (e) {
        handleError(e);
        success = false;
      } finally {
        setSubmitting(false);
      }
      return success;
    },
    [handleError, fetchData, setMessage]
  );

  // -- States ---------------------------------------------------------------

  const [sortState, setSortState] = useState<SortState>({
    column: "nameTh",
    desc: false
  });

  const listItems = useMemo(
    () =>
      data
        ? combineWithMappingData(
            sortList(data.partnerBrands, sortState.column, sortState.desc),
            data.mappings
          )
        : [],
    [data, sortState]
  );

  const searchTermRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const handleChangeSearchTerm = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => setSearchTerm(e.target.value?.trim() ?? "");
  const handleClearSearchTerm = () => {
    if (searchTermRef.current) {
      searchTermRef.current.value = "";
    }
    setSearchTerm(undefined);
  };

  const { value: debouncedSearchTerm, debouncing } = useDebounce(
    searchTerm,
    400
  );

  const [mappedState, setMappedState] = useState<boolean | undefined>();
  const [activeState, setActiveState] = useState<boolean | undefined>();

  const filteredItems = useMemo(
    () =>
      filterListItems(listItems, debouncedSearchTerm, activeState, mappedState),
    [listItems, debouncedSearchTerm, activeState, mappedState]
  );

  // -- Dialogs --------------------------------------------------------------

  const [editState, setEditState] = useState<EditModalState>({ open: false });

  const handleCloseEdit = () => setEditState({ open: false });
  const handleOpenModal = useCallback((item: PartnerBrandWithMapping) => {
    setEditState({
      open: true,
      partnerBrand: item,
      mapping: item.mapping
    });
  }, []);

  // -- Renderers ------------------------------------------------------------

  const handleChangeMapped = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    setMappedState(parseBooleanRadio(value));
  };
  const handleChangeActive = (
    _: React.ChangeEvent<HTMLInputElement>,
    value: string
  ) => {
    setActiveState(parseBooleanRadio(value));
  };

  const rowGetter = useCallback<RowGetter>(
    ({ index }) => filteredItems[index],
    [filteredItems]
  );
  const activeCellRenderer = useCallback<CellRenderer>(
    ({ rowData }) => <ActiveStatus value={rowData.active} />,
    []
  );
  const createdAtCellRenderer = useCallback<CellRenderer>(
    ({ rowData }) => (
      <div className={classes.tableCell}>
        <span
          className={clsx(
            classes.tableCellText,
            !rowData.active && classes.inactive
          )}
        >
          {formatDateTime(rowData.createdAt)}
        </span>
      </div>
    ),
    [classes]
  );
  const updatedAtCellRenderer = useCallback<CellRenderer>(
    ({ rowData }) => (
      <div className={classes.tableCell}>
        <span
          className={clsx(
            classes.tableCellText,
            !rowData.active && classes.inactive
          )}
        >
          {formatDateTime(rowData.updatedAt)}
        </span>
      </div>
    ),
    [classes]
  );
  const mappedAtCellRenderer = useCallback<CellRenderer>(
    ({ rowData }) => (
      <div className={classes.tableCell}>
        <span
          className={clsx(
            classes.tableCellText,
            !rowData.active && classes.inactive
          )}
        >
          {rowData.mappedAt
            ? formatDateTime(rowData.mappedAt)
            : "ยังไม่ได้ map"}
        </span>
      </div>
    ),
    [classes]
  );

  const nameCellRenderer = useCallback<CellRenderer>(
    ({ rowData }) => {
      const { active, mapping } = rowData;
      const mappingLabel = mapping
        ? dropdowns.find(d => d.value === mapping.remyndBrandId)?.label
        : undefined;

      return (
        <div className={classes.tableCell}>
          <div className={classes.tableCell}>
            <IconButton
              aria-label="Edit"
              onClick={() => handleOpenModal(rowData)}
            >
              <Tooltip title={`แก้ไข Mapping`} placement="top">
                <EditIcon />
              </Tooltip>
            </IconButton>
          </div>
          <div
            className={clsx(
              classes.tableCellText,
              classes.ml2,
              !active && classes.inactive
            )}
          >
            {rowData.displayLabel}
            {mappingLabel && (
              <Typography variant="body2" color="primary">
                = {mappingLabel}
              </Typography>
            )}
          </div>
        </div>
      );
    },
    [dropdowns, handleOpenModal, classes]
  );

  return (
    <Paper className={classes.root}>
      <Box m={2}>
        <AsyncDropdown
          label={"Partner"}
          fetchDropdown={listAllPartners}
          paginate={{
            page: 1,
            pageSize: 500,
            order: {
              optionKey: "NAME",
              desc: false
            }
          }}
          value={partner}
          onChange={setPartner}
        />
      </Box>

      <Toolbar className={classes.toolbarRoot1}>
        <div className={classes.toolbarInner}>
          <Button
            className={classes.mr2}
            variant="contained"
            type="button"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => {
              if (!fetching) {
                fetchData().then(() => {
                  setMessage("success", "โหลดข้อมูล สำเร็จ");
                });
              }
            }}
          >
            Reload
          </Button>
        </div>
        <div className={classes.toolbarEnd}>
          <TextField
            className={classes.searchTerm}
            variant="outlined"
            name="searchTerm"
            placeholder="ค้นหาจาก ชื่อ (TH/EN)"
            inputRef={searchTermRef}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear"
                    onClick={handleClearSearchTerm}
                    disabled={!searchTermRef.current?.value}
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              )
            }}
            onChange={handleChangeSearchTerm}
          />
        </div>
      </Toolbar>
      <Toolbar className={classes.toolbarRoot2}>
        <div className={classes.toolbarInner}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="position"
              name="mapped"
              value={formatBooleanRadio(mappedState)}
              onChange={handleChangeMapped}
            >
              <FormControlLabel
                value=""
                control={<Radio color="primary" />}
                label="ทั้งหมด"
              />
              <FormControlLabel
                value="true"
                control={<Radio color="primary" />}
                label="Mapped"
              />
              <FormControlLabel
                value="false"
                control={<Radio color="primary" />}
                label="Not Mapped"
              />
            </RadioGroup>
          </FormControl>
        </div>
        <div className={classes.toolbarEnd}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="position"
              name="active"
              value={formatBooleanRadio(activeState)}
              onChange={handleChangeActive}
            >
              <FormControlLabel
                value=""
                control={<Radio color="primary" />}
                label="ทั้งหมด"
              />
              <FormControlLabel
                value="true"
                control={<Radio color="primary" />}
                label="Active"
              />
              <FormControlLabel
                value="false"
                control={<Radio color="primary" />}
                label="Inactive"
              />
            </RadioGroup>
          </FormControl>
        </div>
      </Toolbar>

      <div className={classes.tableContainer}>
        <VirtualizedTable
          headerHeight={48}
          rowHeight={56}
          rowCount={filteredItems.length}
          rowGetter={rowGetter}
          loading={fetching || submitting || debouncing}
          sortState={sortState}
          setSortState={setSortState}
          columns={[
            {
              width: flexibleWidth,
              label: "ชื่อแบรนด์ Partner",
              dataKey: "nameTh",
              sortKey: "nameTh",
              cellContentRenderer: nameCellRenderer
            },
            {
              width: boolColumnWidth,
              label: "Active",
              dataKey: "active",
              sortKey: "active",
              align: "center",
              cellContentRenderer: activeCellRenderer
            },
            {
              width: dateColumnWidth,
              label: "สร้างเมื่อ",
              dataKey: "createdAt",
              sortKey: "createdAt",
              align: "center",
              cellContentRenderer: createdAtCellRenderer
            },
            {
              width: dateColumnWidth,
              label: "แก้ไขล่าสุดเมื่อ",
              dataKey: "updatedAt",
              sortKey: "updatedAt",
              align: "center",
              cellContentRenderer: updatedAtCellRenderer
            },
            {
              width: dateColumnWidth,
              label: "Map เมื่อ",
              dataKey: "mappedAt",
              align: "center",
              cellContentRenderer: mappedAtCellRenderer
            }
          ]}
        />
      </div>

      {editState.open && (
        <MappingModal
          editState={editState}
          handleCloseModal={handleCloseEdit}
          dropdowns={dropdowns}
          callSubmit={callSubmit}
          callDelete={callDelete}
        />
      )}
    </Paper>
  );
};

export default ListContainers;
